import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  useTheme,
} from '@mui/material';
import { PaperPlaneTiltIcon } from '@phosphor-icons/react';
import { CommentsService, type CommentParent } from '@watchlist/shared';
import { font } from '../../utils/fontHelper';
import { getColors } from '../../tokens/designTokens';
import InitialsAvatar from './InitialsAvatar';

interface CommentComposerProps {
  parent: CommentParent;
  currentUserId: string;
  currentUserName: string;
  currentUserAvatar?: string | null;
  onCommentAdded?: (commentId: string) => void;
  /** Forwarded to the InputBase onFocus — wire useKeyboardAware().scrollIntoViewOnFocus here. */
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const CommentComposer: React.FC<CommentComposerProps> = ({
  parent,
  currentUserId,
  currentUserName,
  currentUserAvatar,
  onCommentAdded,
  onInputFocus,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = getColors(isDark);

  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const trimmed = text.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const id = await CommentsService.addComment(
        parent,
        trimmed,
        currentUserId,
        currentUserName,
        currentUserAvatar ?? null
      );
      setText('');
      onCommentAdded?.(id);
    } catch (err) {
      console.error('CommentComposer: failed to add comment', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      gap: 1.5,
      alignItems: 'flex-end',
      pt: 1.5,
      borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    }}>
      <Box sx={{ flexShrink: 0, mb: 0.5 }}>
        <InitialsAvatar
          src={currentUserAvatar ?? undefined}
          name={currentUserName}
          size={32}
        />
      </Box>

      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'flex-end',
        borderRadius: 3,
        bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        px: 1.5,
        py: 0.75,
        gap: 0.5,
      }}>
        <InputBase
          multiline
          maxRows={4}
          fullWidth
          placeholder="Add a comment…"
          value={text}
          onChange={e => { if (e.target.value.length <= 500) setText(e.target.value); }}
          inputProps={{ maxLength: 500 }}
          onKeyDown={handleKeyDown}
          onFocus={onInputFocus}
          disabled={submitting}
          sx={{
            fontFamily: font(),
            fontSize: 14,
            color: colors.text.primary,
            '& .MuiInputBase-input': {
              '&::placeholder': {
                color: colors.text.secondary,
                opacity: 1,
              },
            },
          }}
        />
        <IconButton
          size="small"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            color: canSubmit ? theme.palette.primary.main : colors.text.secondary,
            flexShrink: 0,
            mb: -0.25,
          }}
        >
          {submitting
            ? <CircularProgress size={18} />
            : <PaperPlaneTiltIcon size={18} />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default CommentComposer;
